"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PriorityQueue = void 0;
const ArrayEnumerable_1 = require("../enumerables/ArrayEnumerable");
const EnumerableFactory_1 = require("../utilities/EnumerableFactory");
const utilityFunctions_1 = require("../utilities/utilityFunctions");
const List_1 = require("./List");
/**
 * Implements an array-backed min-heap.
 * Each element is enqueued with an associated priority that determines the dequeue order: elements with the lowest priority get dequeued first.
 */
class PriorityQueue extends ArrayEnumerable_1.ArrayEnumerable {
    constructor(collectionOrComparer, comparer) {
        const src = [];
        super(new EnumerableFactory_1.EnumerableFactory(), (0, utilityFunctions_1.getIterableGenerator)(src), src);
        this.srcComparer =
            typeof collectionOrComparer === 'function'
                ? collectionOrComparer
                : comparer !== null && comparer !== void 0 ? comparer : ((a, b) => {
                    if (a > b) {
                        return 1;
                    }
                    else if (a < b) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                });
        if (typeof collectionOrComparer !== 'function' && collectionOrComparer) {
            for (const [item, priority] of collectionOrComparer) {
                this.enqueue(item, priority);
            }
        }
    }
    get comparer() {
        return this.srcComparer;
    }
    get length() {
        return this.srcArr.length;
    }
    get unorderedItems() {
        return new List_1.List(this);
    }
    clear() {
        this.srcArr.length = 0;
    }
    copyTo(array, arrayIndex) {
        let i = arrayIndex;
        for (const item of this) {
            array[i] = item;
            i++;
        }
    }
    dequeue() {
        const item = this.tryDequeue();
        if (item === null) {
            throw new Error('Queue has no items.');
        }
        return item;
    }
    enqueue(item, priority) {
        this.srcArr.push([item, priority]);
        this.heapifyUp();
    }
    enqueueRange(items, priority) {
        if (priority !== undefined) {
            for (const item of items) {
                this.enqueue(item, priority);
            }
        }
        else {
            for (const [item, itemPriority] of items) {
                this.enqueue(item, itemPriority);
            }
        }
    }
    peek() {
        const item = this.tryPeek();
        if (item === null) {
            throw new Error('Queue has no items.');
        }
        return item;
    }
    tryDequeue() {
        if (this.srcArr.length === 0) {
            return null;
        }
        const result = this.peek();
        const tail = this.srcArr.pop();
        if (tail === undefined) {
            throw new Error('Internal heap error.');
        }
        if (this.srcArr.length > 0) {
            this.srcArr[0] = tail;
            this.heapifyDown();
        }
        return result;
    }
    tryPeek() {
        if (this.srcArr.length === 0) {
            return null;
        }
        return this.srcArr[0][0];
    }
    heapifyUp() {
        let index = this.length - 1;
        while (this.hasParent(index) &&
            this.srcComparer(this.srcArr[index][1], this.srcArr[this.getParentIndex(index)][1]) < 0) {
            this.swap(index, this.getParentIndex(index));
            index = this.getParentIndex(index);
        }
    }
    heapifyDown() {
        let index = 0;
        // Because a heap is a complete tree, if a node has a child, it will always have at least a left child.
        while (this.hasLeftChild(index)) {
            let smallerChildIndex = this.getLeftChildIndex(index);
            // Check if the right child is smaller than left.
            if (this.hasRightChild(index) &&
                this.srcComparer(this.srcArr[this.getRightChildIndex(index)][1], this.srcArr[smallerChildIndex][1]) < 0) {
                smallerChildIndex = this.getRightChildIndex(index);
            }
            if (this.srcComparer(this.srcArr[index][1], this.srcArr[smallerChildIndex][1]) < 0) {
                break;
            }
            this.swap(index, smallerChildIndex);
            index = smallerChildIndex;
        }
    }
    hasParent(index) {
        return index > 0;
    }
    hasLeftChild(index) {
        return this.getLeftChildIndex(index) < this.length;
    }
    hasRightChild(index) {
        return this.getRightChildIndex(index) < this.length;
    }
    getParentIndex(index) {
        return Math.floor((index - 1) / 2);
    }
    getLeftChildIndex(index) {
        return Math.floor(2 * index + 1);
    }
    getRightChildIndex(index) {
        return Math.floor(2 * index + 2);
    }
    swap(index1, index2) {
        const temp = this.srcArr[index1];
        this.srcArr[index1] = this.srcArr[index2];
        this.srcArr[index2] = temp;
    }
}
exports.PriorityQueue = PriorityQueue;
//# sourceMappingURL=PriorityQueue.js.map